import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ReactNode, Ref, memo } from 'react';
import { TextInput, TextInputProps, View } from 'react-native';
import { voiceInputColors } from '../voiceInputColors';

/** The box grows with the text up to about this many lines, then scrolls. */
const kMaxLines = 5;

/**
 * The composer's text box with its mic. One line, sent by the keyboard's
 * send key (`onSubmit`); or growing with the text, where the return key
 * starts a new line and the host's own button sends.
 */
export const TextBox = memo(
  ({
    inputRef,
    value,
    onChangeText,
    placeholder,
    onSubmit,
    autoFocus,
    onBlur,
    inputProps,
    mic,
  }: {
    inputRef?: Ref<TextInput>;
    value: string;
    onChangeText: (text: string) => void;
    placeholder: string;
    /** Set: one line, and the send key sends. */
    onSubmit: (() => void) | null;
    autoFocus: boolean;
    onBlur: () => void;
    inputProps?: Omit<
      TextInputProps,
      'value' | 'onChangeText' | 'placeholder' | 'multiline'
    >;
    mic: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const single = onSubmit != null;
    const lineHeight = rpx(20);
    return (
      <View
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: rpx(44),
          flexDirection: 'row',
          // Several lines: the mic stays at the bottom, by the last one.
          alignItems: single ? 'center' : 'flex-end',
          gap: rpx(6),
          borderRadius: rpx(22),
          paddingLeft: rpx(16),
          paddingRight: rpx(4),
          paddingVertical: single ? 0 : rpx(4),
          backgroundColor: voiceInputColors.input,
        }}
      >
        <TextInput
          ref={inputRef}
          {...inputProps}
          value={value}
          onChangeText={onChangeText}
          autoFocus={autoFocus}
          onBlur={event => {
            inputProps?.onBlur?.(event);
            onBlur();
          }}
          placeholder={placeholder}
          placeholderTextColor={voiceInputColors.mutedForeground}
          multiline={!single}
          {...(single
            ? {
                onSubmitEditing: onSubmit,
                returnKeyType: 'send' as const,
                blurOnSubmit: false,
              }
            : {})}
          style={{
            flex: 1,
            fontSize: rpx(14),
            lineHeight: single ? undefined : lineHeight,
            color: voiceInputColors.foreground,
            padding: 0,
            ...(single
              ? { height: rpx(44) }
              : {
                  // iOS sizes a multiline input to its text between these,
                  // its padding included.
                  minHeight: lineHeight + rpx(16),
                  maxHeight: lineHeight * kMaxLines + rpx(16),
                  paddingVertical: rpx(8),
                }),
          }}
        />
        {mic}
      </View>
    );
  },
);
